import { initLogin } from './pages/login';
import { initDashboard, refreshDashboard } from './pages/dashboard';
import { initWizard } from './pages/order-wizard';
import { getAssignedOrderCount, initDB /*, saveOrder*/ } from './services/db';
import { performServerSync } from './services/sync';
//import { Workbox } from 'workbox-window';
import { renderLayout } from './components/layout';
import { updateFooterSyncStatus } from './components/footer';
import { showLoadingOverlay, updateLoadingMessage, hideLoadingOverlay, showLoadingError } from './components/loading-overlay';
//import type { Order } from './models/order';
import { isAuthenticated } from './services/auth';
import type { UserState } from './models/userState';
import { ROUTE_NAME_DASHBOARD, ROUTE_NAME_HOME_LANDING } from './routing/routes';
import { findMatchingRoute, NAVIGATION_EVENT, type RouteMatch } from './routing/router';
import { initHelp } from './pages/help';
//import { getDefaultAddress, type Address } from './models/address';

// Initialize Service Worker for PWA features (offline, background sync)
if ('serviceWorker' in navigator) {

  /*
  const wb = new Workbox('/sw.js');

  wb.addEventListener('waiting', () => {
    if (confirm('New content is available. Refresh now?')) {
      wb.addEventListener('controlling', () => {
        window.location.reload();
      });
      wb.messageSkipWaiting();
    }
  });

  wb.addEventListener('activated', (event) => {
    if (!event.isUpdate) {
      console.log('App is ready to work offline');
      document.getElementById('sync-status')!.textContent = 'Sync: Ready Offline';
    }
  });

  wb.register();
  */
}

// Database initialization constants
const DB_INIT_TIMEOUT_MS = 15000; // 15 seconds timeout
let dbInitPromise: Promise<void> | null = null;

// User state management to help with navigation.
let currentUserState: UserState = {
  currentUserId: null,
  currentRouteName: ROUTE_NAME_HOME_LANDING,
  lastSyncDate: null,
};

//TODO this can be moved to a user state management module.
// the state could also link with a close window event to persist state on exit.
export function getCurrentUserState(): UserState {
  return currentUserState;
}

export function setCurrentUserState(newState: Partial<UserState>): void {
  currentUserState = { ...currentUserState, ...newState };
}

// Show database initialization status
function showDBStatus(message: string, isError: boolean = false) {
  const statusEl = document.getElementById('db-status');
  if (statusEl) {
    statusEl.textContent = message;
    statusEl.style.color = isError ? '#dc3545' : '#28a745';
    statusEl.style.fontWeight = 'bold';
    if (!isError) {
      // Hide success message after 3 seconds
      setTimeout(() => {
        statusEl.textContent = '';
      }, 3000);
    }
  }
}

/**
 * Initialize database with timeout and error handling
 */
async function initializeDatabase(): Promise<void> {

  if (dbInitPromise) {
    return dbInitPromise;
  }

  dbInitPromise = new Promise<void>(async (resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Database initialization timeout - please check your connection and try again'));
    }, DB_INIT_TIMEOUT_MS);

    try {
      updateLoadingMessage('Loading database...');
      await initDB();
      clearTimeout(timeoutId);
      //console.log('✓ Database ready - App can work offline');
      showDBStatus('✓ Database ready - App can work offline');
      resolve();
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Database initialization failed:', error);
      showDBStatus('✗ Database initialization failed. Please refresh the page.', true);
      reject(error);
    }
  });

  return dbInitPromise;
}

// Track last sync time
let lastSyncTime: Date | undefined;

// Routing logic based on URL
async function initApp() {
  try {
    console.log('initApp - Initializing application...');
    updateLoadingMessage('Initializing application...');
    
    currentUserState = getCurrentUserState();


    const path = window.location.pathname;

    // Check authentication status using auth service
    const isUserAuthenticated = isAuthenticated();

    if (!isUserAuthenticated && path !== '/login') {
      window.location.href = '/login';
      return;
    }

    // Wait for database initialization before proceeding
    //console.log('Loading database...');
    updateLoadingMessage('Loading database...');
    await initializeDatabase();

    
    // Now render the layout (buttons will be available after this)
    //console.log('Loading interface...');
    updateLoadingMessage('Loading interface...');
    renderLayout("");
    
    
    

    // Manual sync function
    async function performSync() {
      //console.log('Starting manual sync...');

      if (!navigator.onLine) {
        console.log('Cannot sync while offline');
        return;
      }
      
      const syncBtn = document.getElementById('sync-btn') as HTMLButtonElement;
      if (syncBtn) {
        syncBtn.disabled = true;
        syncBtn.textContent = 'Syncing...';
      }
      
      try {
        await dbInitPromise;


        await performServerSync();

        lastSyncTime = new Date();
        updateFooterSyncStatus(true, lastSyncTime);
        console.log('Sync Orders completed successfully');

       

        
        //TODO we can show a toast notification here instead of alert
        
        // Refresh dashboard if user is currently on the dashboard page
        const currentPath = window.location.pathname;
        if (currentPath === '/dashboard' || currentPath === '/') {
          await refreshDashboard();
          console.log('✓ Dashboard data refreshed after sync');
        }

      } catch (error) {
        console.error('Sync error:', error);
        alert('Sync failed. Please try again.');
      } finally {
        if (syncBtn) {
          syncBtn.disabled = false;
          syncBtn.textContent = 'Sync Now';
        }
      }
    }

    // Update sync status based on online status
    async function updateSyncStatus() {
      const isOnline = navigator.onLine;
      updateFooterSyncStatus(isOnline, lastSyncTime);
      
      // Don't auto-sync anymore, only manual sync via button
    }
    
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);

    // Update sync status
    await updateSyncStatus();

    

    // Route handling
    updateLoadingMessage('Loading content...');

    // Perform initial navigation and route matching
    const currentRouteMatch = await performNavigationEvent();

    // Update user state with current route
    if (currentRouteMatch) {
      setCurrentUserState({ 
        currentRouteName: currentRouteMatch.route.title 
      });
    }

    //Now initialise the main buttons that require auth/layout to be ready
    // Manual sync button handler
    const syncBtn = document.getElementById('sync-btn');
    if (syncBtn) {

      if(currentRouteMatch?.route.title === ROUTE_NAME_HOME_LANDING || currentRouteMatch?.route.title === ROUTE_NAME_DASHBOARD) {
        syncBtn.style.display = "block";
      }
      else{
        syncBtn.style.display = "none";
      }

      syncBtn.addEventListener('click', performSync);
    }
    else {
      console.warn('Sync button not found in DOM.');
    }

    // Export database button handler
    const exportBtn = document.getElementById('export-db-btn');
    if (exportBtn) {
      exportBtn.addEventListener('click', async () => {
        try {

          console.log('Exporting database...');

          const { exportDatabase } = await import('./services/db');
          const dbData = await exportDatabase();
          
          // Create download link with proper ArrayBuffer
          const arrayBuffer = dbData.buffer.slice(0) as ArrayBuffer;
          const blob = new Blob([arrayBuffer], { type: 'application/x-sqlite3' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          
          // Generate filename with timestamp
          const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
          link.href = url;
          link.download = `walkinbath-orders-${timestamp}.db`;
          
          // Trigger download
          document.body.appendChild(link);
          link.click();
          
          // Cleanup
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          //console.log('Database exported successfully');

        } catch (error) {
          console.error('Export error:', error);
          alert('Failed to export database. Please try again.');
        }
      });
    }
    else {
      //console.warn('Export DB button not found in DOM.');
    }

    // Set up navigation event listener for SPA-style navigation
    window.addEventListener(NAVIGATION_EVENT, async (/*event: Event*/) => {
      //const customEvent = event as CustomEvent;
      //console.log('Navigation event triggered:', customEvent.detail.path);
      
      // Show brief loading state
      showLoadingOverlay('Loading...');
      
      try {
        // Perform navigation and re-render
        const newRouteMatch = await performNavigationEvent();
        
        // Update user state with new route
        if (newRouteMatch) {
          setCurrentUserState({ currentRouteName: newRouteMatch.route.title });
          
          // Update sync button visibility based on route
          const syncBtn = document.getElementById('sync-btn');
          if (syncBtn) {
            if (newRouteMatch.route.title === ROUTE_NAME_HOME_LANDING || newRouteMatch.route.title === ROUTE_NAME_DASHBOARD) {
              syncBtn.style.display = "block";
            } else {
              syncBtn.style.display = "none";
            }
          }
        }
        
        hideLoadingOverlay();
      } catch (error) {
        console.error('Navigation error:', error);
        hideLoadingOverlay();
        alert('Navigation failed. Please try again.');
      }
    });

    // All initialization complete - hide loading overlay
    hideLoadingOverlay();
    
    console.log('init app completed successfully. ');

  } catch (error) {
    console.error('App initialization error:', error);
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred during initialization';
    showLoadingError(errorMessage, () => {
      // Retry by reloading the page
      window.location.reload();
    });
  }
}

/**
 * Handles navigation events and renders the appropriate page content
 * Uses the new router to match routes with URL parameters
 * @returns RouteMatch object with matched route and parameters, or null if no match
 */
async function performNavigationEvent(): Promise<RouteMatch | null> {
  const content = document.getElementById('app-content')!;
  
  // Find matching route using the new router
  const routeMatch = findMatchingRoute(window.location.pathname);
  
  if (!routeMatch) {
    // No matching route found - show 404
    content.innerHTML = '<h2>404 - Page Not Found</h2><a href="/">Back to Dashboard</a>';
    return null;
  }
  
  const { route, params } = routeMatch;
  
  // Route to the appropriate page based on route title
  if (route.url === '/login') {
    initLogin();
  } else if (route.url === '/help') {
    initHelp();
  } else if (route.url === '/dashboard' || route.url === '/') {
    await initDashboard();
  } else if (route.url === '/wizard/{id}') {
    // Extract the order ID from the URL parameters
    const orderId = parseInt(params.id, 10);
    if (isNaN(orderId)) {
      content.innerHTML = '<h2>Invalid Order ID</h2><a href="/dashboard">Back to Dashboard</a>';
    } else {
      initWizard(orderId);
      updateProgressBar(0); // Start with step 0
    }
  }
  
  return routeMatch;
}

// Progress bar update function for wizard
function updateProgressBar(step: number) {
  const path = window.location.pathname;
  const progressBar = document.getElementById('progress-bar')!;
  if(!progressBar) return;
  const progress = document.getElementById('progress')!;
  progressBar.style.display = path.startsWith('/wizard/') ? 'block' : 'none';
  progress.style.width = `${(step / 5) * 100}%`; // 6 steps (0-5)
}

// Handle browser navigation (back/forward buttons)
window.addEventListener('popstate', async () => {
  showLoadingOverlay('Loading...');
  
  try {
    // Perform navigation and re-render
    const newRouteMatch = await performNavigationEvent();
    
    // Update user state with new route
    if (newRouteMatch) {
      setCurrentUserState({ currentRouteName: newRouteMatch.route.title });
      
      // Update sync button visibility based on route
      const syncBtn = document.getElementById('sync-btn');
      if (syncBtn) {
        if (newRouteMatch.route.title === ROUTE_NAME_HOME_LANDING || newRouteMatch.route.title === ROUTE_NAME_DASHBOARD) {
          syncBtn.style.display = "block";
        } else {
          syncBtn.style.display = "none";
        }
      }
    }
    
    hideLoadingOverlay();
  } catch (error) {
    console.error('Navigation error:', error);
    hideLoadingOverlay();
  }
});

// Initial load
document.addEventListener('DOMContentLoaded', async () => {
  // Show loading overlay immediately
  showLoadingOverlay('Starting application...');
  
  try {
    await initApp();

    // Example: Create a test order if none exist (for development)
    if (isAuthenticated() && (await getAssignedOrderCount() === 0)) {

      //TODO - Good indication a sales rep needs to sync their orders.

    }
  } catch (error) {
    // Error already handled in initApp, but log here for debugging
    console.error('DOMContentLoaded error:', error);
  }
});
